'use client';

import { useCallback } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { canAccessRoute } from '@/features/auth';
import { useAppSettings } from '@/features/configurations';
import { useFirstAccessibleRoute } from '@/hooks/use-nav';

/**
 * Returns a `canAccess(path)` predicate for the signed-in account.
 *
 * Use it to hide any affordance that navigates somewhere else — a row action,
 * a card link, a notification target. Showing a link to a screen the account
 * can't open is what produces a full-page "Access Denied" one click later.
 */
export function useRouteAccess(): (path: string) => boolean {
  const { user } = useAuth();
  const settings = useAppSettings();

  return useCallback(
    (path: string) => canAccessRoute(user, path, settings),
    [user, settings]
  );
}

/**
 * Resolves where to actually send someone: the intended screen, or their first
 * accessible one when they can't open it.
 *
 * For the redirects that follow an action rather than a click — "created, now
 * back to the list", "handed off, now open the next stage". A role can hold
 * `*.create` without `*.view`, and then a successful save landed on a listing
 * that refused it, which reads as "saving is broken".
 */
export function useAccessibleRoute(): (preferred: string) => string {
  const canAccess = useRouteAccess();
  const firstRoute = useFirstAccessibleRoute();

  return useCallback(
    (preferred: string) => (canAccess(preferred) ? preferred : firstRoute),
    [canAccess, firstRoute]
  );
}
